import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type UIEvent,
} from 'react';

import { Icon } from '../../../components/Icon';
import type { TableSourceDescriptor, VectorSourceDescriptor } from '../../../data/model';
import {
  createTableColumns,
  formatTableCell,
  nextTableSort,
  sortRowIndices,
  tableRowIdentity,
  tableSourceRowKey,
  type TableSort,
} from '../../../data/table/attributeTable';
import { virtualRowWindow } from '../../../data/table/virtualRows';
import { fieldDisplayLabel, isJoinedEffectiveField } from '../../../joins/effectiveLayer';
import type { LayerDefinition } from '../../../project/model';
import { evaluateAttributeRule } from '../../../rules/evaluate';
import { selectedFeatureRef } from '../../../selection/model';
import { useAppStore } from '../../state/useAppStore';

const ROW_HEIGHT = 30;
const DEFAULT_VIEWPORT_HEIGHT = 240;
const ROW_OVERSCAN = 8;
const ROW_NUMBER_WIDTH = 52;
const ROW_SELECTION_WIDTH = 40;

type AttributeTableView = 'all' | 'selected';

export type AttributeTableContext =
  | {
      readonly kind: 'vector';
      readonly layer: LayerDefinition;
      /** Effective derived source when joins exist; canonical identity/order are preserved. */
      readonly source: VectorSourceDescriptor;
    }
  | { readonly kind: 'table'; readonly source: TableSourceDescriptor };

interface ContextAttributeTablePanelProps {
  readonly context: AttributeTableContext;
  readonly onClose: () => void;
}

interface VectorAttributeTablePanelProps {
  readonly layer: LayerDefinition;
  readonly source: VectorSourceDescriptor;
  readonly onClose: () => void;
}

type AttributeTablePanelProps = ContextAttributeTablePanelProps | VectorAttributeTablePanelProps;

function countLabel(count: number, singular: string): string {
  return `${count.toLocaleString()} ${count === 1 ? singular : `${singular}s`}`;
}

function sortIndicator(sort: TableSort | null, field: string): string {
  if (sort?.field !== field) return '';
  return sort.direction === 'ascending' ? '↑' : '↓';
}

function AttributeTablePanelComponent(props: AttributeTablePanelProps) {
  const { onClose } = props;
  const context: AttributeTableContext =
    'context' in props
      ? props.context
      : { kind: 'vector', layer: props.layer, source: props.source };
  const source = context.source;
  const vector = context.kind === 'vector' ? context : null;
  const title = vector?.layer.name ?? source.name;
  const rowCount = source.kind === 'vector' ? source.featureCount : source.rowCount;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const verticalScrollTopRef = useRef(0);
  const [sort, setSort] = useState<TableSort | null>(null);
  const [tableView, setTableView] = useState<AttributeTableView>('all');
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(DEFAULT_VIEWPORT_HEIGHT);
  const selectedFeatures = useAppStore((state) => state.shell.selectedFeatures);
  const toggleFeatureSelection = useAppStore((state) => state.toggleFeatureSelection);
  const clearLayerSelection = useAppStore((state) => state.clearLayerSelection);
  const selectedForLayer = useMemo(
    () =>
      vector
        ? selectedFeatures.filter(
            (feature) =>
              feature.layerId === vector.layer.id && feature.sourceId === vector.source.id,
          )
        : [],
    [selectedFeatures, vector],
  );
  const selectedKeys = useMemo(
    () => new Set(selectedForLayer.map((feature) => feature.key)),
    [selectedForLayer],
  );
  const selectedSourceIndices = useMemo(
    () => new Set(selectedForLayer.map((feature) => feature.sourceFeatureIndex)),
    [selectedForLayer],
  );
  const columns = useMemo(() => createTableColumns(source.fields), [source.fields]);
  const tableColumnIndices = useMemo(
    () => new Map(source.fields.map((field, index) => [field.name, index])),
    [source.fields],
  );
  const valueAt = useCallback(
    (rowIndex: number, field: string): unknown => {
      if (source.kind === 'vector') return source.data.features[rowIndex]?.properties?.[field];
      const column = tableColumnIndices.get(field);
      return column === undefined ? undefined : source.rows[rowIndex]?.[column];
    },
    [source, tableColumnIndices],
  );
  const sortedRowOrder = useMemo(
    () => sortRowIndices(rowCount, source.fields, sort, valueAt),
    [rowCount, sort, source.fields, valueAt],
  );
  const rowOrder = useMemo(
    () =>
      vector && tableView === 'selected'
        ? sortedRowOrder.filter((rowIndex) => selectedSourceIndices.has(rowIndex))
        : sortedRowOrder,
    [selectedSourceIndices, sortedRowOrder, tableView, vector],
  );
  const leadingWidth = ROW_NUMBER_WIDTH + (vector ? ROW_SELECTION_WIDTH : 0);
  const gridTemplateColumns = useMemo(
    () =>
      `${vector ? `${ROW_SELECTION_WIDTH}px ` : ''}${ROW_NUMBER_WIDTH}px ${columns
        .map((column) => `${column.width}px`)
        .join(' ')}`,
    [columns, vector],
  );
  const gridWidth = useMemo(
    () => leadingWidth + columns.reduce((total, column) => total + column.width, 0),
    [columns, leadingWidth],
  );
  const window = virtualRowWindow(
    rowOrder.length,
    scrollTop,
    viewportHeight,
    ROW_HEIGHT,
    ROW_OVERSCAN,
  );
  const visibleRows = rowOrder.slice(window.start, window.end);
  const gridStyle = {
    '--attribute-grid-columns': gridTemplateColumns,
    minWidth: `${gridWidth}px`,
  } as CSSProperties;

  useLayoutEffect(() => {
    const scrollArea = scrollRef.current;
    if (!scrollArea) return;
    const updateHeight = () =>
      setViewportHeight(scrollArea.clientHeight || DEFAULT_VIEWPORT_HEIGHT);
    updateHeight();
    if (typeof ResizeObserver !== 'function') return;
    const observer = new ResizeObserver(updateHeight);
    observer.observe(scrollArea);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!vector || selectedForLayer.length !== 1) return;
    const selected = selectedForLayer[0];
    if (!selected) return;
    const displayIndex = rowOrder.indexOf(selected.sourceFeatureIndex);
    const scrollArea = scrollRef.current;
    if (displayIndex < 0 || !scrollArea) return;
    const rowTop = displayIndex * ROW_HEIGHT;
    const rowBottom = rowTop + ROW_HEIGHT;
    if (
      rowTop >= scrollArea.scrollTop &&
      rowBottom <= scrollArea.scrollTop + scrollArea.clientHeight
    )
      return;
    const nextScrollTop = Math.max(0, rowTop - ROW_HEIGHT);
    scrollArea.scrollTop = nextScrollTop;
    verticalScrollTopRef.current = nextScrollTop;
    setScrollTop(nextScrollTop);
  }, [rowOrder, selectedForLayer, vector]);

  const resetVerticalScroll = () => {
    verticalScrollTopRef.current = 0;
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setScrollTop(0);
  };

  const closeAndRestoreFocus = () => {
    const fallback =
      document.querySelector<HTMLElement>('.inspector-data .data-table-action button') ??
      document.querySelector<HTMLElement>('.layer-row-main[aria-pressed="true"]') ??
      document.querySelector<HTMLElement>('.table-row-main[aria-pressed="true"]');
    onClose();
    requestAnimationFrame(() => {
      if (fallback?.isConnected) fallback.focus();
    });
  };

  return (
    <section className="attribute-table-panel" aria-label={`Attribute table for ${title}`}>
      <header className="attribute-table-panel-header">
        <div>
          <h2>Data — {title}</h2>
          <p>
            {countLabel(rowCount, source.kind === 'vector' ? 'feature' : 'row')} ·{' '}
            {countLabel(source.fields.length, 'field')}
            {vector?.layer.displayFilter ? ' · Map filter active' : ''}
          </p>
        </div>
        <div className="attribute-table-header-controls">
          {vector ? (
            <div aria-label="Table rows" className="attribute-table-view-toggle" role="group">
              <button
                aria-pressed={tableView === 'all'}
                onClick={() => {
                  setTableView('all');
                  resetVerticalScroll();
                }}
                type="button"
              >
                All
              </button>
              <button
                aria-pressed={tableView === 'selected'}
                onClick={() => {
                  setTableView('selected');
                  resetVerticalScroll();
                }}
                type="button"
              >
                Selected ({selectedForLayer.length.toLocaleString()})
              </button>
            </div>
          ) : null}
          <div className="attribute-table-header-actions">
            {vector && selectedForLayer.length > 0 ? (
              <button
                aria-label={`Clear selection for ${vector.layer.name}`}
                onClick={() => clearLayerSelection(vector.layer.id)}
                type="button"
              >
                Clear selection
              </button>
            ) : null}
            <button
              aria-label="Close attribute table"
              className="icon-button"
              onClick={closeAndRestoreFocus}
              type="button"
            >
              <Icon name="close" />
            </button>
          </div>
        </div>
      </header>

      {source.fields.length === 0 ||
      rowCount === 0 ||
      (vector && tableView === 'selected' && rowOrder.length === 0) ? (
        <div className="attribute-table-empty" role="status">
          {source.fields.length === 0
            ? 'This source has no attribute fields to inspect.'
            : rowCount === 0
              ? `This source contains 0 ${source.kind === 'vector' ? 'features' : 'rows'}.`
              : 'No selected features in this layer.'}
        </div>
      ) : (
        <div
          aria-label={`Scrollable attributes for ${title}`}
          className="attribute-table-scroll"
          onScroll={(event: UIEvent<HTMLDivElement>) => {
            const next = event.currentTarget.scrollTop;
            if (next !== verticalScrollTopRef.current) {
              verticalScrollTopRef.current = next;
              setScrollTop(next);
            }
          }}
          ref={scrollRef}
          tabIndex={0}
        >
          <div
            aria-colcount={source.fields.length + (vector ? 2 : 1)}
            aria-rowcount={rowOrder.length + 1}
            className="attribute-table-grid"
            data-table-view={vector ? tableView : 'table'}
            role="grid"
            style={gridStyle}
          >
            <div className="attribute-table-header-row" role="row">
              {vector ? (
                <div className="attribute-table-selection-header" role="columnheader">
                  <span className="visually-hidden">Selection</span>
                </div>
              ) : null}
              <div className="attribute-table-row-number-header" role="columnheader">
                <span className="visually-hidden">Row number</span>
              </div>
              {columns.map((column) => {
                const activeSort = sort?.field === column.field.name ? sort.direction : 'none';
                const label = fieldDisplayLabel(column.field);
                const joined = isJoinedEffectiveField(column.field);
                return (
                  <div
                    aria-sort={activeSort}
                    className={`attribute-table-column-header${joined ? ' attribute-table-column-joined' : ''}`}
                    key={column.field.name}
                    role="columnheader"
                    title={`${label} · ${column.field.type}`}
                  >
                    <button
                      aria-label={`Sort ${label}; currently ${activeSort}`}
                      onClick={() => {
                        setSort((current) => nextTableSort(current, column.field.name));
                        resetVerticalScroll();
                      }}
                      type="button"
                    >
                      <span>{label}</span>
                      <span aria-hidden="true" className="attribute-table-sort-indicator">
                        {sortIndicator(sort, column.field.name)}
                      </span>
                    </button>
                  </div>
                );
              })}
            </div>
            <div
              className="attribute-table-row-space"
              style={{ height: `${rowOrder.length * ROW_HEIGHT}px` }}
            >
              {visibleRows.map((rowIndex, visibleIndex) => {
                const displayIndex = window.start + visibleIndex;
                const feature = source.kind === 'vector' ? source.data.features[rowIndex] : null;
                if (source.kind === 'vector' && !feature) return null;
                const key = feature
                  ? tableRowIdentity(source.id, feature, rowIndex).key
                  : tableSourceRowKey(source.id, rowIndex);
                const selectionRef =
                  vector && feature
                    ? selectedFeatureRef(vector.layer.id, source.id, feature, rowIndex)
                    : null;
                const selected = selectionRef ? selectedKeys.has(selectionRef.key) : false;
                const selectable =
                  vector && feature
                    ? evaluateAttributeRule(vector.layer.displayFilter, feature.properties)
                    : false;
                return (
                  <div
                    aria-rowindex={displayIndex + 2}
                    aria-selected={vector ? selected : undefined}
                    className={
                      selected
                        ? 'attribute-table-row attribute-table-row-selected'
                        : 'attribute-table-row'
                    }
                    data-attribute-row="true"
                    data-row-key={key}
                    data-source-feature-index={vector ? rowIndex : undefined}
                    data-source-row-index={!vector ? rowIndex : undefined}
                    key={key}
                    role="row"
                    style={{
                      height: `${ROW_HEIGHT}px`,
                      top: `${displayIndex * ROW_HEIGHT}px`,
                    }}
                  >
                    {vector && selectionRef ? (
                      <div className="attribute-table-selection-cell" role="gridcell">
                        <input
                          aria-label={
                            selectable
                              ? `Select feature row ${displayIndex + 1} in ${vector.layer.name}`
                              : `Feature row ${displayIndex + 1} is filtered out on map`
                          }
                          checked={selected}
                          disabled={!selectable}
                          onChange={() => toggleFeatureSelection(selectionRef)}
                          type="checkbox"
                        />
                      </div>
                    ) : null}
                    <div className="attribute-table-row-number" role="rowheader">
                      {displayIndex + 1}
                    </div>
                    {columns.map((column) => {
                      const displayedValue = formatTableCell(valueAt(rowIndex, column.field.name));
                      return (
                        <div
                          className="attribute-table-cell"
                          key={column.field.name}
                          role="gridcell"
                          title={displayedValue}
                        >
                          {displayedValue}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export const AttributeTablePanel = memo(AttributeTablePanelComponent);
